// The beam walk, mirrored from the server (R548).
//
// A beam travels through water, so a ridge casts a shadow: the reach of a
// Beacon is NOT the geometric disc `hex-ring.ts` draws. The server settles
// that in `Sim.beamHalo` (`server/internal/sim/sov.go`), and card 46 puts
// the real reach on the chart, ring by ring. This leaf is the hand mirror of
// that walk, and `beam-halo.test.ts` asserts it against a golden fixture the
// Go package itself writes — the pattern `world.vectors_test.go` set (R72).
//
// THE FOUR RULES THE MIRROR MUST KEEP:
//
//  1. A `BLOCK_RIDGE` cell STOPS the walk. It is never visited, and nothing
//     passes through it. A trench cell is water and passes.
//  2. The origin is visited at ring 0 whatever stands on it. The server
//     visits it before it looks at the mask at all.
//  3. The cells of one hop are visited in ASCENDING cell index, because the
//     server sorts each hop. "Closest, then lowest cell id" is a rule and
//     not an accident.
//  4. A cell is visited once, at the first hop that reaches it.
//
// Regenerate the fixture after an intentional change to the server's walk:
//
//   cd server && AQUA_REGEN_VECTORS=1 go test ./internal/sim -run TestGoldenHalo
//
// then re-run the client vitest in the same change.

import { BLOCK_RIDGE, neighborAt } from '../world';

/** One visit of the walk: the cell, and the hop that reached it. */
export interface HaloVisit {
  cell: number;
  ring: number;
}

/**
 * Walks the water within `rings` of `origin`, hop by hop, and calls `visit`
 * with each cell and its hop.
 *
 * `blocked` is the hello's terrain mask, one byte per cell: 0 open,
 * `BLOCK_TRENCH`, `BLOCK_RIDGE`. A mask shorter than the grid reads as open
 * water past its end, which is what a caller with no terrain yet wants.
 */
export function beamHalo(
  origin: number,
  rings: number,
  blocked: Uint8Array,
  cols: number,
  rows: number,
  visit: (cell: number, ring: number) => void,
): void {
  const cells = cols * rows;
  if (origin < 0 || origin >= cells) return;
  const seen = new Uint8Array(cells);
  seen[origin] = 1;
  visit(origin, 0);
  let frontier: number[] = [origin];
  for (let hop = 1; hop <= rings && frontier.length > 0; hop++) {
    const next: number[] = [];
    for (const c of frontier) {
      for (let k = 0; k < 6; k++) {
        const nb = neighborAt(c, k, cols, rows);
        if (nb < 0) continue;
        if (seen[nb] !== 0 || blocked[nb] === BLOCK_RIDGE) continue;
        seen[nb] = 1;
        next.push(nb);
      }
    }
    next.sort((a, b) => a - b);
    for (const c of next) visit(c, hop);
    frontier = next;
  }
}

/**
 * The whole walk as a list, in visit order — the origin first, then each hop
 * in ascending cell index.
 *
 * This is the form a chart piece wants: card 46 outlines the cumulative set
 * of every hop up to k, and the ring a cell carries is what puts it in the
 * right set.
 */
export function haloCells(
  origin: number, rings: number, blocked: Uint8Array, cols: number, rows: number,
): HaloVisit[] {
  const out: HaloVisit[] = [];
  beamHalo(origin, rings, blocked, cols, rows, (cell, ring) => out.push({ cell, ring }));
  return out;
}
